/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/invoiceData.scss';

import {ChangeFunction, ChangeKeyFunction, Loader, preventDefault, WithBackground} from '@dvrd/dvr-controls';
import React, {MouseEventHandler, useContext, useMemo} from 'react';
import Invoice, {InvoiceFields} from "../../../../models/invoiceModel";
import {ErrorResetter, InvoiceDataErrors} from "../../../../util/interfaces";
import {BolmateButton, BolmateInput, BolmateNumberInput} from "../../../controls";
import {AppContext} from "../../../misc/appContext";
import CountryField from "../../../controls/country/field/countryField";

interface Props {
    onClose: MouseEventHandler;
    onChange: ChangeKeyFunction<keyof InvoiceFields, string>;
    onChangeInvoiceNumber: ChangeFunction<string>;
    onChangeInvoiceNumberPrefix: ChangeFunction<string>;
    onResetError: ErrorResetter<InvoiceDataErrors>;
    onSubmit: (upload: boolean) => MouseEventHandler;
    invoice: Invoice;
    loading: boolean;
    errors: InvoiceDataErrors;
    active: boolean;
    invoiceNumber: string;
    invoiceNumberPrefix: string;
}

export default function InvoiceData(props: Props) {
    const {account} = useContext(AppContext).accountContext;
    const {
        onClose, onChange, onChangeInvoiceNumber, onChangeInvoiceNumberPrefix, onResetError, onSubmit, invoice, loading,
        errors, active, invoiceNumber, invoiceNumberPrefix
    } = props;
    const lastUsed: string | null = useMemo(() => {
        if (!account) return null;
        return account.getFlex('last_used_invoice_number') ?? null;
    }, [account]);

    function renderBody() {
        return (
            <form className='body-container' onSubmit={preventDefault}>
                <Loader active={loading}/>
                {renderExplanation()}
                <div className='divider'/>
                {renderInvoiceNumber()}
                <div className='divider'/>
                {renderData()}
            </form>
        )
    }

    function renderExplanation() {
        return (
            <div className='invoice-number-container'>
                <label className='part-title'>Waarom moet ik dit handmatig doen?</label>
                <label className='message'>Deze factuur is waarschijnlijk van voor gebruik Bolmate. We krijgen hierbij
                    (vanwege de AVG) waarschijnlijk niet alle klantgegevens. Deze moet je zelf daarom handmatig nog
                    aanvullen. Doordat de Belastingdienst vereist dat jouw factuurnummering chronologisch oploopt,
                    kunnen we hiervoor niet het standaard factuurnummer gebruiken.</label>
            </div>
        )
    }

    function renderInvoiceNumber() {
        return (
            <div className='invoice-number-container'>
                <label className='part-title'>1) Kies factuurnummer</label>
                <label className='message'>Kies een factuurnummer voor deze factuur. Het factuurnummer mag niet eerder
                    gebruikt zijn en de facturen moeten met oplopende factuurnummers genummerd worden. Lees hierboven
                    waarom.</label>
                {!!lastUsed && <label><b>Laatste keer vulde je in:</b> {lastUsed}</label>}
                <div className='input-row'>
                    <BolmateInput onChange={onChangeInvoiceNumberPrefix} value={invoiceNumberPrefix} label='Voorvoegsel'
                                  inputProps={{placeholder: '{jaar}oud-'}}
                                  error={errors.invoiceNumberPrefix} onFocus={onResetError('invoiceNumberPrefix')}/>
                    <BolmateNumberInput onChange={onChangeInvoiceNumber} value={invoiceNumber} label='Factuurnummer'
                                        inputProps={{placeholder: '0001'}}
                                        onFocus={onResetError('invoiceNumber')} error={errors.invoiceNumber}
                                        wholeNumbers/>
                </div>
            </div>
        )
    }

    function renderData() {
        return (
            <div className='invoice-data-container'>
                <label className='part-title'>2) Controleer factuurgegevens en vul aan</label>
                <label className='message'>Deze gegevens zijn ingevuld bij de factuuraanvraag en worden gebruikt voor op
                    de factuur. Je kunt deze nog aanpassen voordat de factuur aangemaakt wordt.</label>
                <div className='row'>
                    <BolmateInput onChange={onChange('firstname')} value={invoice.firstname} label='Voornaam'
                                  placeholder='Voornaam' error={errors.firstname} onFocus={onResetError('firstname')}/>
                    <BolmateInput onChange={onChange('lastname')} value={invoice.lastname} label='Achternaam'
                                  placeholder='Achternaam' error={errors.lastname} onFocus={onResetError('lastname')}/>
                </div>
                <div className='row'>
                    <BolmateInput onChange={onChange('street')} value={invoice.street} label='Straat'
                                  placeholder='Straat' error={errors.address} onFocus={onResetError('address')}/>
                    <BolmateInput onChange={onChange('house_number')} value={invoice.houseNumber} label='Huisnummer'
                                  placeholder='Huisnummer' error={errors.houseNumber}
                                  onFocus={onResetError('houseNumber')}/>
                </div>
                <div className='row'>
                    <BolmateInput onChange={onChange('zip_code')} value={invoice.zipCode} label='Postcode'
                                  placeholder='Postcode' error={errors.zipCode} onFocus={onResetError('zipCode')}/>
                    <BolmateInput onChange={onChange('city')} value={invoice.city} label='Woonplaats'
                                  placeholder='Woonplaats' error={errors.city} onFocus={onResetError('city')}/>
                </div>
                <CountryField onChange={onChange('country')} value={invoice.countryName} label='Land' labelAsValue/>
                <BolmateInput onChange={onChange('company')} value={invoice.company} label='Bedrijfsnaam'
                              placeholder='optioneel'/>
                <div className='row'>
                    <BolmateInput onChange={onChange('coc_number')} value={invoice.cocNumber} label='KvK-nummer'
                                  placeholder='optioneel'/>
                    <BolmateInput onChange={onChange('vat_number')} value={invoice.vatNumber} label='BTW-nummer'
                                  placeholder='optioneel'/>
                </div>
            </div>
        )
    }

    return (
        <WithBackground active={active} onClose={onClose} preventBackgroundClick>
            <div className='default-popup invoice-request-data'>
                <div className='header-container'>
                    <label className='header-title'>Handmatig factuur aanmaken voor order {invoice.orderId}</label>
                </div>
                {renderBody()}
                <div className='footer-container'>
                    <BolmateButton onClick={onClose} label='Annuleren' disabled={loading} secondary/>
                    <div className='submit-buttons'>
                        <BolmateButton onClick={onSubmit(false)} label='Factuur aanmaken' disabled={loading} secondary/>
                        <BolmateButton onClick={onSubmit(true)} label='Factuur aanmaken & uploaden' disabled={loading}/>
                    </div>
                </div>
            </div>
        </WithBackground>
    )
}